// Services
import breedingManagementService from "@/app/services/main/breeding-management/breeding-management-service.js";

// Heat Cycle Info
import HEAT_CYCLE_INFO from "@/app/views/main-view/breeding-management/gestation-period/constants/heat-cycle.info.js";

// Mixins
import { dateMixins } from "@/app/mixins/utils/date-mixins";
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { unsavedChangesModalMixins } from "@/app/mixins/utils/unsaved-changes-mixins.js";

// Third Party libraries
import DatePicker from "vue2-datepicker";

// Constants
import { STATUS_CODES, SUCCESS_MSG, WHELPING_DATE_STATUS } from "@/app/constants/status-constants";
import { BREEDER_ROUTES } from "@/app/constants/breeder-routes-constants";

export default {
    name: "GestationPeriodContainer",
    props: {
        damId: {
            type: Number,
            required: false,
            default: null,
        },
        matingId: {
            type: Number,
            required: false,
            default: null,
        },
        isDamDisabled: {
            type: Boolean,
            default: false,
        },
        littersCount: {
            type: Number,
            default: 0,
            required: false,
        },
    },
    components: {
        DatePicker,
    },
    mixins: [dateMixins, toastMessageMixins, unsavedChangesModalMixins],
    data() {
        return {
            isDisableAllFunction: false,
            isAPILoading: false,
            isAPISubmitLoading: false,
            HEAT_CYCLE_INFO,

            damDetails: null,
            firstDayOfCycle: null,
            ovulationCycleRange: [],
            diaryDetails: [],

            matingDate: null,
            whelpingDatesRange: [],
            whelpingDatesRangeLegend: null,

            whelpingDate: null,

            previousWhelpingDates: [],
            previousWhelpingDatesToRemove: [],

            prevRoute: "Dam Details",

            maxLittersCount: 15,
            selectedBirthType: null,
            typeOfBirthList: ["Natural", "C-section" ],
            showTypeOfBirthSelect: false,
            whelpingDateErr: false
        };
    },
    watch: {
        damId: {
            deep: true,
            async handler(newValue) {
                // reset all cycle dates values
                if (!newValue) {
                    this.firstDayOfCycle = null;
                    this.$emit("onChangeFirstDayOfCycle", { isInitial: false, firstDayOfCycle: null });
                    this.ovulationCycleRange = [];

                    this.matingDate = null;
                    this.$emit("onChangeMatingDate", { isInitial: false, matingDate: null });
                    this.whelpingDatesRange = [];
                    this.whelpingDatesRangeLegend = "";

                    this.whelpingDate = null;
                    this.$emit("onChangeWhelpingDate", { isInitial: false, whelpingDate: null });

                    this.diaryDetails = [];

                    return;
                }

                await this.getDamOvulationAndGestationDetails(newValue, false);
                // await this.getDiaryDetails(newValue);
            },
        },
        previousWhelpingDates: {
            deep: true,
            async handler(newValue) {
                this.$emit("onChangePreviousWhelpingDates", { isInitial: false, previousWhelpingDates: newValue, isDisableAllFunction: this.isDisableAllFunction  });
            },
        },
    },
    computed: {
        dogImageSource() {
            return this.damDetails && this.damDetails.imageUrl ? this.damDetails.imageUrl : require("@/assets/images/_shared/dog-purchase.png");
        },
        redirectToAddLitterLinkText() {
            if (this.maxLittersCount === this.littersCount) {
                return "View Litter";
            }

            // if (!this.littersCount) {
            //     return "Add Litter";
            // }

            return "Add Litter";
            // return "Add / View Litter";
        },

        filteredPuppyStatusOptions() {
            return this.puppyStatusOptions.filter((status) => status !== this.matingStatus);
        },
    },
    async created() {
        if (this.damId) {
            if (this.$route.query.from === "mating-details") {
                this.prevRoute = "Mating Details";
            }else if (this.$route.query.from === "mating-list") {
                this.prevRoute = "Mating List";
            }
            await this.getDamOvulationAndGestationDetails(this.damId, true);
            // await this.getDiaryDetails(this.damId);
        }
    },
    methods: {
        async onSubmitDetails() {
            try {
                this.isAPISubmitLoading = true;

                if (!this.firstDayOfCycle && !this.matingDate) {
                    await this.$swal({
                        text: "Please provide either the first day of the cycle or the mating date.",
                        imageHeight: "30px",
                        imageUrl: `${require("@/assets/images/_shared/sad-dog.png")}`,
                        imageAlt: "Sad Dog",
                        confirmButtonText: "Got it",
                        showCloseButton: true,
                    });

                    return;
                }

                const { SUCCESS_ADD_OVULATION_GESTATION, ERROR_ADD_OVULATION_GESTATION } = this.toastMessages.ovulationGestation();

                // format dates to properly save them (not index 0)
                const detailsToSave = {
                    damId: this.damId,
                    ovulationStartDate: this.firstDayOfCycle ? this.formatDateWithoutConversion(this.firstDayOfCycle, "yyyy-MM-dd") : null,
                    matingDate: this.matingDate ? this.formatDateWithoutConversion(this.matingDate, "yyyy-MM-dd") : null,
                };

                const response = await breedingManagementService.saveOvulationAndGestation(detailsToSave);

                if (response.status === STATUS_CODES.SUCCESS_POST) {
                    this.showCustomSuccessToast(SUCCESS_MSG.SUCCESS_TITLE, SUCCESS_ADD_OVULATION_GESTATION);
                    this.isAddingSuccess = true;

                    await this.getDamOvulationAndGestationDetails(this.damId, true);
                    // await this.getDiaryDetails(this.damId);
                } else {
                    this.showAPIErrorToast(ERROR_ADD_OVULATION_GESTATION, response);
                }
            } catch (err) {
                this.showGenericErrorToast();
            } finally {
                this.isAPISubmitLoading = false;
            }
        },
        async getDamOvulationAndGestationDetails(damId, isInitial = false) {
            if (!damId) return;

            try {
                this.isAPILoading = true;

                const detailsResponse = await breedingManagementService.getDamOvulationAndGestationDetails(damId, this.matingId);

                if (detailsResponse.status === STATUS_CODES.SUCCESS) {
                    this.damDetails = detailsResponse.data;
                    const { ovulationDetails, gestationDetails, previousWhelpingDates, whelpingDate } = detailsResponse.data;

                    if (ovulationDetails) {
                        const { startDate: firstDayOfCycle, endDate: lastDayOfCycle } = ovulationDetails;

                        this.firstDayOfCycle = firstDayOfCycle ? new Date(firstDayOfCycle) : null;
                        this.$emit("onChangeFirstDayOfCycle", { isInitial, firstDayOfCycle: this.firstDayOfCycle });

                        if (firstDayOfCycle && lastDayOfCycle) {
                            this.ovulationCycleRange = [new Date(firstDayOfCycle), new Date(lastDayOfCycle)];
                        }
                    } else {
                        this.firstDayOfCycle = null;
                        this.$emit("onChangeFirstDayOfCycle", { isInitial, firstDayOfCycle: null });
                        this.ovulationCycleRange = [];
                    }

                    if (gestationDetails) {
                        const { matingDate, whelpingPeriodStart, whelpingPeriodEnd } = gestationDetails;

                        this.matingDate = matingDate ? new Date(matingDate) : null;
                        this.$emit("onChangeMatingDate", { isInitial, matingDate: this.matingDate });

                        if (whelpingPeriodStart && whelpingPeriodEnd) {
                            this.whelpingDatesRange = [new Date(whelpingPeriodStart), new Date(whelpingPeriodEnd)];
                            // compute the range based on the values selected
                            this.whelpingDatesRangeLegend =
                                Array.isArray(this.whelpingDatesRange) && this.whelpingDatesRange.length === 2
                                    ? [...this.whelpingDatesRange].map((date) => this.formatDateWithoutConversion(date)).join(" ~ ")
                                    : "N/A";
                        }
                    } else {
                        this.matingDate = null;
                        this.$emit("onChangeMatingDate", { isInitial, matingDate: null });
                        this.whelpingDatesRange = [];
                        this.whelpingDatesRangeLegend = "";
                    }

                    if (detailsResponse.data.whelpingDate) {
                        this.isDisableAllFunction = detailsResponse.data.whelpingDate.typeOfBirth  &&  this.prevRoute === "Mating List";
                        this.showTypeOfBirthSelect = this.isDisableAllFunction
                    } else if (detailsResponse.data.previousWhelpingDates) {
                        this.isDisableAllFunction = detailsResponse.data.previousWhelpingDates.typeOfBirth  &&  this.prevRoute === "Mating List";
                        this.showTypeOfBirthSelect = this.isDisableAllFunction
                    }

                    if (previousWhelpingDates) {
                        this.previousWhelpingDates = previousWhelpingDates.map((data) => {
                            return { ...data, date: new Date(data.whelpingDate) };
                        });

                        this.$emit("onChangePreviousWhelpingDates", { isInitial, previousWhelpingDates, isDisableAllFunction: this.isDisableAllFunction });
                    }

                    if (whelpingDate && whelpingDate.whelpingDate) {
                        this.whelpingDate = new Date(whelpingDate.whelpingDate);
                        this.$emit("onChangeWhelpingDate", { isInitial, whelpingDate: this.whelpingDate, isDisableAllFunction: this.isDisableAllFunction });
                    } else {
                        this.$emit("onChangeWhelpingDate", { isInitial, whelpingDate: this.whelpingDate, isDisableAllFunction: this.isDisableAllFunction });
                    }


                    this.selectedBirthType = whelpingDate && whelpingDate.typeOfBirth ?whelpingDate.typeOfBirth :  null

                    this.$nextTick(() => {
                        this.formatOvulationCycleCalendar("ovulation-cycle-calendar");
                        this.formatOvulationCycleCalendar("whelping-dates-calendar");
                    });

                } else {
                    const { ERROR_GET_DAM_SIRE } = this.toastMessages.damSire();
                    this.showAPIErrorToast(ERROR_GET_DAM_SIRE, detailsResponse);
                }
            } catch (err) {
                this.showGenericErrorToast();
            } finally {
                this.isAPILoading = false;
            }
        },
        shouldDisplay(value) {
            if (this.isDisableAllFunction && this.selectedBirthType) {
                return this.selectedBirthType === value
            } else {
                return true
            }
        },
        // async getDiaryDetails(damId) {
        //     const { ERROR_FETCHING_PREVIOUS_OVULATION } = this.toastMessages.ovulationGestation();

        //     try {
        //         const response = await breedingManagementService.getDamDiaryDetails(damId);
        //         this.diaryDetails = [...response.data];
        //     } catch (error) {
        //         this.showGenericErrorToast();
        //     }
        // },
        onChangeFirstDayOfCycle() {
            if (!this.firstDayOfCycle) {
                this.$emit("onChangeFirstDayOfCycle", { isInitial: false, firstDayOfCycle: null });
                return;
            }

            const endDate = new Date(this.firstDayOfCycle);

            // Add 20 days
            endDate.setDate(endDate.getDate() + 20);

            this.ovulationCycleRange = [new Date(this.firstDayOfCycle), new Date(endDate)];

            // this will ensure the DOM is ready before manually adding some updates
            this.$nextTick(() => {
                this.formatOvulationCycleCalendar("ovulation-cycle-calendar");
            });

            this.$emit("onChangeFirstDayOfCycle", { isInitial: false, firstDayOfCycle: this.firstDayOfCycle });
        },
        onChangeMatingDate() {
            if (!this.matingDate) {
                this.$emit("onChangeMatingDate", { isInitial: false, matingDate: null });
                return;
            }

            const startDate = new Date(this.matingDate);
            const endDate = new Date(this.matingDate);

            startDate.setDate(startDate.getDate() + 60);
            endDate.setDate(endDate.getDate() + 65);

            this.whelpingDatesRange = [startDate, endDate];

            this.whelpingDatesRangeLegend =
                Array.isArray(this.whelpingDatesRange) && this.whelpingDatesRange.length === 2 ? [...this.whelpingDatesRange].map((date) => this.formatDateWithoutConversion(date)).join(" ~ ") : "N/A";

            // this will ensure the DOM is ready before manually adding some updates
            this.$nextTick(() => {
                this.formatOvulationCycleCalendar("whelping-dates-calendar");
            });

            this.$emit("onChangeMatingDate", { isInitial: false, matingDate: this.matingDate });
        },

        onTypeOfBirthSelect (birthType) {
            const  whelpingDateTmp = this.whelpingDate ? this.whelpingDate: null
            const  selectedBirthTypeTmp = birthType ? birthType: null
            if(whelpingDateTmp){
                this.$emit("onTypeOfBirthSelect", { isInitial: false, whelpingDate: whelpingDateTmp, typeOfBirth: selectedBirthTypeTmp});
            } else {
                this.whelpingDateErr = true
            }
             
        },

        formatOvulationCycleCalendar(wrapperClass) {
            const dataTableWrapper = document.querySelector(`.${wrapperClass}`);

            if (dataTableWrapper) {
                const cells = dataTableWrapper.querySelectorAll(".cell");

                if (cells && cells.length) {
                    [...cells].forEach((cell) => {
                        cell.classList.add("reset-highlight");
                        cell.classList.remove("highlight-active");
                    });
                }

                const cellsInRangeOrActive = dataTableWrapper.querySelectorAll(".cell.in-range, .cell.active");

                if (cellsInRangeOrActive && cellsInRangeOrActive.length) {
                    // exclude the cells which have the class below to not affect their styling
                    const toModifyRange = [...cellsInRangeOrActive].filter((cell) => {
                        return !cell.classList.contains("not-current-month");
                    });

                    // this makes sure that only get the first to 21st element
                    const slicedArray = [...toModifyRange].slice(0, 21);

                    // only select the last four elements
                    [...slicedArray].slice(-4).forEach((cell) => {
                        cell.classList.add("highlight-active");
                    });

                    [...cellsInRangeOrActive].forEach((cell) => {
                        cell.classList.remove("reset-highlight");

                        if (cell.classList.contains("not-current-month")) {
                            // manually reset the styling of them, overriding its default behavior
                            cell.classList.add("reset-highlight");
                        }
                    });
                }

                // these are the calendar popups
                const dateTables = dataTableWrapper.querySelectorAll(".mx-calendar.mx-calendar-panel-date");
                if (dateTables && dateTables.length) {
                    [...dateTables].forEach((table) => {
                        // Select cells with either .in-range or .active class within the current table, exclude cells with class not(.not-current-month)
                        const cellsInTableRangeOrActive = table.querySelectorAll(".cell.in-range:not(.not-current-month), .cell.active:not(.not-current-month)");

                        // hide the table if they don't have an active cell or it's not in range
                        if (!cellsInTableRangeOrActive.length) {
                            table.classList.add("hide-table");
                        } else {
                            table.classList.remove("hide-table");
                        }
                    });
                }
            }
        },
        onChangeWhelpingDate() {
            this.whelpingDateErr = false
            if (!this.whelpingDate) {
                this.$emit("onChangeWhelpingDate", { isInitial: false, whelpingDate: null });
                return;
            }

            this.$emit("onChangeWhelpingDate", { isInitial: false, whelpingDate: this.whelpingDate });
        },
        notFutureOfMatingDate(date) {
            if (!this.matingDate) {
                return false;
            }

            // disable dates after the mating date
            return date > new Date(this.matingDate);
        },
        notPastFirstDayOfCycle(date) {
            if (!this.firstDayOfCycle) {
                return false;
            }

            // disable dates before the first day of the cycle
            return date < new Date(this.firstDayOfCycle);
        },
        notPastMatingDateCycle(date) {
            if (!this.matingDate) {
                return false;
            }

            // disable dates before the mating date
            return date < new Date(this.matingDate);
        },
        diaryTimelineItems() {
            let items = [];
            const dateFormat = "MMMM dd, yyyy";
            for (const data of this.diaryDetails) {
                let content = "";
                content += `<p class="mb-0"><span style="font-weight: 700 !important;">1st day of cycle:</span> ${
                    data.ovulationCycleDate ? this.formatDateTime(data.ovulationCycleDate, dateFormat) : "N/A"
                }</p>`;
                content += `<p class="mb-0"><span style="font-weight: 700 !important;">Mating date:</span> ${
                    data.gestationTrackerDate ? this.formatDateTime(data.gestationTrackerDate, dateFormat) : "N/A"
                }</p>`;
               
                content += `<p class="mb-0"><span style="font-weight: 700 !important;">Whelping date:</span> ${data.whelpingDate ? this.formatDateTime(data.whelpingDate, dateFormat) : "N/A"}</p>`;
                content += `<p class="mb-0"><span style="font-weight: 700 !important;">Type of Birth:</span> ${data.typeOfBirth ? data.typeOfBirth : "N/A"}</p>`;

                items.push({
                    htmlMode: true,
                    content: content,
                });
            }
            return items;
        },
        addWhelpingDate() {
            const newWhelpingDate = {
                id: Math.random().toString(),
                date: null,
            };

            this.previousWhelpingDates.push(newWhelpingDate);
        },
        removeWhelpingDate(whelpingDate) {
            if (!whelpingDate) {
                return;
            }

            this.previousWhelpingDates = this.previousWhelpingDates.filter((dateObj) => dateObj.id !== whelpingDate.id);

            if (whelpingDate.id && whelpingDate.status === WHELPING_DATE_STATUS.ACTIVE) {
                this.previousWhelpingDatesToRemove.push(whelpingDate.id);
                this.$emit("onChangePreviousWhelpingDatesToRemove", { isInitial: false, previousWhelpingDatesToRemove: this.previousWhelpingDatesToRemove, isDisableAllFunction: this.isDisableAllFunction  });
            }
        },
        notAfterToday(date) {
            return date > new Date();
        },
        redirectToAddLitter() {
            if (!this.matingId) {
                return;
            }
            if (parseFloat(this.littersCount) === parseFloat(this.maxLittersCount)) {
                this.$router.push(`${BREEDER_ROUTES.LITTER_LIST}?id=${this.matingId}&from=mating-details`);
            } else {
                this.$router.push(`${BREEDER_ROUTES.ADD_LITTER}?id=${this.matingId}&from=mating-details`);
            }
        },
        displayBirthType () {
            this.showTypeOfBirthSelect = !this.showTypeOfBirthSelect
        }
    },
    async beforeRouteLeave(to, from, next) {
        /*
        const isFirstDayOfCycleChanged = (this.initialValues.firstDayOfCycle ? this.initialValues.firstDayOfCycle.getTime() : null) !== (this.firstDayOfCycle ? this.firstDayOfCycle.getTime() : null);
        const isMatingDateChanged = (this.initialValues.matingDate ? this.initialValues.matingDate.getTime() : null) !== (this.matingDate ? this.matingDate.getTime() : null);

        const isDataChanged = isFirstDayOfCycleChanged || isMatingDateChanged;

        if (isDataChanged) {
            const result = await this.showUnsavedChangesModal();

            if (result.value) {
                next(true);
            } else {
                next(false);
            }
        } else {
            next(true);
        } */
    },
};
